import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Zap, TrendingUp } from 'lucide-react'
import { useLoyaltyPoints, useLoyaltyLevel, useRecentPointsGain, useLoyaltyStore } from '../../stores/loyaltyStore'
import { ACHIEVEMENT_LEVELS } from '../../types'

interface PointsDisplayProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showAnimation?: boolean
  onClick?: () => void
}

const PointsDisplay = ({
  className = '',
  size = 'md',
  showAnimation = true,
  onClick
}: PointsDisplayProps) => {
  const totalPoints = useLoyaltyPoints()
  const currentLevel = useLoyaltyLevel()
  const recentGain = useRecentPointsGain()
  const { clearRecentPointsGain } = useLoyaltyStore()
  const [showGainAnimation, setShowGainAnimation] = useState(false)

  // Get current level achievement data for styling
  const currentAchievement = ACHIEVEMENT_LEVELS[currentLevel]

  // Handle recent points gain animation
  useEffect(() => {
    if (recentGain && showAnimation) {
      setShowGainAnimation(true)

      const timer = setTimeout(() => {
        setShowGainAnimation(false)
        clearRecentPointsGain()
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [recentGain, showAnimation, clearRecentPointsGain])

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      text: 'text-xs',
      badge: 'text-xs px-1.5 py-0.5'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      text: 'text-sm',
      badge: 'text-xs px-2 py-1'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      text: 'text-base',
      badge: 'text-sm px-2.5 py-1'
    }
  }

  const config = sizeConfig[size]

  return (
    <div className={`relative ${className}`}>
      {/* Main Points Display */}
      <motion.div
        className={`
          inline-flex items-center gap-2
          bg-white/40 backdrop-blur-xl rounded-2xl border border-white/20
          shadow-premium-sm hover:bg-white/50 hover:shadow-premium-md
          transition-all duration-300 cursor-pointer
          ${config.container}
        `}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        style={{
          boxShadow: `0 0 20px ${currentAchievement.frameColor}20`
        }}
        role="button"
        tabIndex={0}
        aria-label={`Tienes ${totalPoints} puntos de lealtad. Nivel actual: ${currentAchievement.name}. Click para abrir panel de lealtad.`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
      >
        {/* Level Badge */}
        <div
          className={`
            flex items-center justify-center rounded-xl
            ${config.badge} font-bold text-white shadow-lg
          `}
          style={{
            background: `linear-gradient(135deg, ${currentAchievement.frameColor}, ${currentAchievement.frameColor}dd)`
          }}
          aria-hidden="true"
        >
          <Zap className={`${config.icon} mr-1`} />
          {currentLevel.charAt(0).toUpperCase()}
        </div>

        {/* Points Counter */}
        <div className="flex flex-col">
          <motion.span
            key={totalPoints}
            initial={showAnimation ? { scale: 1.2 } : false}
            animate={{ scale: 1 }}
            className={`font-bold text-gray-900 ${config.text} leading-tight`}
          >
            {totalPoints.toLocaleString()}
          </motion.span>
          <span className="text-xs text-gray-500 leading-none -mt-0.5">
            puntos
          </span>
        </div>

        {/* Level Indicator */}
        <div
          className="w-2 h-8 rounded-full opacity-60"
          style={{
            background: `linear-gradient(to top, ${currentAchievement.frameColor}, ${currentAchievement.frameColor}80)`
          }}
          aria-hidden="true"
        />
      </motion.div>

      {/* Recent Points Gain Animation */}
      <AnimatePresence>
        {showGainAnimation && recentGain && (
          <motion.div
            initial={{ opacity: 0, y: 0, scale: 0.8 }}
            animate={{
              opacity: [0, 1, 1, 0],
              y: [-10, -20, -25, -35],
              scale: [0.8, 1, 1, 0.9]
            }}
            exit={{ opacity: 0, y: -40, scale: 0.8 }}
            transition={{
              duration: 3,
              times: [0, 0.2, 0.8, 1],
              ease: "easeOut"
            }}
            className="absolute -top-2 left-1/2 transform -translate-x-1/2 pointer-events-none z-50"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-lg shadow-lg">
              <TrendingUp className="w-3 h-3" aria-hidden="true" />
              <span>+{recentGain.amount}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screen Reader Only Information */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {recentGain && (
          <span>
            Has ganado {recentGain.amount} puntos por {recentGain.source}.
            Total actual: {totalPoints} puntos.
          </span>
        )}
      </div>
    </div>
  )
}

export default PointsDisplay